import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Bell, CheckCheck } from 'lucide-react';

const NotificationsPage = () => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    getNotificationIcon,
    getNotificationColor
  } = useNotifications();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Notifications</h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            {unreadCount > 0 ? (
              <>
                <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
                You have {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
              </>
            ) : (
              "You're all caught up!"
            )}
          </p>
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-all shadow-sm active:scale-95"
          >
            <CheckCheck size={18} className="text-blue-500" />
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-500 animate-pulse">Retrieving your updates...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-gray-200 text-center px-6">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
            <Bell className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications yet</h3>
          <p className="text-gray-500 max-w-xs">
            We'll notify you when something important happens regarding your orders or account.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {notifications.map((n) => {
            const colorClass = getNotificationColor(n.notification_type);
            const isRead = n.read;

            return (
              <div
                key={n.id}
                className={`group relative overflow-hidden bg-white rounded-2xl border transition-all duration-300 hover:shadow-md ${isRead
                    ? 'border-gray-100 opacity-90'
                    : 'border-blue-100 shadow-sm'
                  }`}
              >
                {/* Status Indicator Bar */}
                {!isRead && (
                  <div className={`absolute top-0 left-0 bottom-0 w-1 ${colorClass}`}></div>
                )}

                <div className="p-4 sm:p-5 flex items-start gap-4">
                  {/* Icon Wrapper */}
                  <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-xl sm:text-2xl ${isRead ? 'bg-gray-50 text-gray-400' : 'bg-blue-50 text-blue-600'
                    }`}>
                    {getNotificationIcon(n.notification_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`text-base sm:text-lg font-semibold truncate pr-4 ${isRead ? 'text-gray-700' : 'text-gray-900 font-bold'}`}>
                        {n.title}
                      </h4>
                      <span className="text-[11px] sm:text-xs text-gray-400 whitespace-nowrap bg-gray-50 px-2 py-1 rounded-full uppercase tracking-wider font-medium">
                        {formatDate(n.created_at)}
                      </span>
                    </div>

                    <p className={`text-sm sm:text-base leading-relaxed ${isRead ? 'text-gray-500' : 'text-gray-600'}`}>
                      {n.message}
                    </p>

                    {!isRead && (
                      <div className="mt-4 flex items-center gap-3">
                        <button
                          onClick={() => markAsRead(n.id)}
                          className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 transition-colors py-1 px-3 rounded-full hover:bg-blue-50"
                        >
                          Mark as read
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Unread Dot */}
                  {!isRead && (
                    <div className="absolute top-5 right-5 sm:relative sm:top-0 sm:right-0">
                      <span className="flex h-2.5 w-2.5 rounded-full bg-blue-500 ring-4 ring-blue-50 shadow-sm"></span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;


